import {
  SERVER_TRACE_DEFAULT_SAMPLE_RATE,
  SERVER_TRACE_DEFAULT_SLOW_THRESHOLD_MS,
} from './constants';

const clampSampleRate = (value: number) => Math.min(Math.max(value, 0), 1);

const parseSampleRate = (value: string | undefined, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? clampSampleRate(parsed) : fallback;
};

/**
 * 동일 request ID가 항상 같은 샘플링 결과를 갖도록 결정적 해시를 계산합니다.
 * 단순 무작위 추출과 달리 여러 위치에서 판정하더라도 결과가 바뀌지 않습니다.
 */
const hashRequestId = (requestId: string) => {
  let hash = 2166136261;

  for (let index = 0; index < requestId.length; index += 1) {
    hash ^= requestId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

/**
 * `KC_PERFORMANCE_LOGGING=true`일 때만 trace 수집과 출력을 활성화합니다.
 */
export const isServerTraceEnabled = () => process.env.KC_PERFORMANCE_LOGGING === 'true';

/**
 * 정상 요청의 기본 샘플링 여부를 결정합니다.
 *
 * trace가 활성화된 환경의 기본값은 100%이며 `KC_SERVER_TRACE_SAMPLE_RATE`의
 * 0~1 값으로 덮어쓸 수 있습니다. `KC_PERFORMANCE_LOGGING`이 `true`가 아니면
 * 샘플링 전에 전체 trace가 비활성화됩니다. 느린 요청과 오류 요청은 이 결과와 별도로
 * store의 최종 출력 조건에서 다시 포함됩니다.
 */
export const shouldSampleServerTrace = (requestId: string) => {
  if (!isServerTraceEnabled()) return false;

  const sampleRate = parseSampleRate(
    process.env.KC_SERVER_TRACE_SAMPLE_RATE,
    SERVER_TRACE_DEFAULT_SAMPLE_RATE,
  );

  if (sampleRate === 0) return false;
  if (sampleRate === 1) return true;

  return hashRequestId(requestId) / 0xffffffff < sampleRate;
};

/**
 * 샘플링에서 제외됐더라도 기록할 느린 요청의 기준값을 반환합니다.
 */
export const getServerTraceSlowThresholdMs = () => {
  const parsed = Number(process.env.KC_SERVER_TRACE_SLOW_MS);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return SERVER_TRACE_DEFAULT_SLOW_THRESHOLD_MS;
  }

  return parsed;
};
